import Papa from "papaparse";
import type { SaleRow } from "./types";

// El CSV exportado desde Numbers a veces tiene una primera fila vacía
// (porque las columnas en Numbers viven en la fila 2). Manejamos los
// dos casos: header en fila 1 o header en fila 2.
//
// Columnas que buscamos (case-insensitive, trim):
//   - TRANSPORTISTA       (requerido)
//   - CLIENTE             (requerido)
//   - COD. CLIENTE        (opcional pero recomendado para uniqueClients)
//   - EA.LATITUD / LAT    (requerido — coordenadas)
//   - EA.LONGITUD / LON   (requerido)
//   - Suma de VALOR VENTA / VENTA (requerido)

// `id` no se alista porque no viene del CSV — lo asignamos nosotros
// al construir cada fila. Lo omitimos del Record con Omit<>.
const HEADER_ALIASES: Record<Exclude<keyof SaleRow, "id"> | "_skip", string[]> = {
  sede: ["sede"],
  canal: ["canal"],
  fEmision: ["f. emision", "f.emision", "fecha emision", "f_emision"],
  transportista: ["transportista"],
  vendedor: ["vendedor"],
  secuencia: ["secuencia"],
  codCliente: ["cod. cliente", "cod cliente", "codigo cliente", "cod_cliente"],
  cliente: ["cliente"],
  lat: ["ea.latitud", "ea latitud", "latitud", "lat"],
  lon: ["ea.longitud", "ea longitud", "longitud", "lon", "lng"],
  valorVenta: ["suma de valor venta", "valor venta", "valor_venta", "venta"],
  // M10.1: peso/volumen del pedido (opcionales). Si no vienen, el
  // optimizador hace fallback al proxy valor_venta×factor.
  pesoKg: ["peso", "peso_kg", "peso kg", "weight", "weight_kg"],
  volumenM3: ["volumen", "volumen_m3", "volumen m3", "volume", "volume_m3"],
  _skip: [],
};

function matchHeader(raw: string): Exclude<keyof SaleRow, "id"> | null {
  const norm = raw.trim().toLowerCase();
  for (const [key, aliases] of Object.entries(HEADER_ALIASES) as [
    Exclude<keyof SaleRow, "id"> | "_skip",
    string[],
  ][]) {
    if (key === "_skip") continue;
    if (aliases.some((a) => a === norm)) return key as Exclude<keyof SaleRow, "id">;
  }
  return null;
}

export type ParseError =
  | { kind: "missing-headers"; missing: string[] }
  | { kind: "no-data" }
  | { kind: "papaparse"; message: string };

export type ParseSuccess = {
  rows: SaleRow[];
  warnings: string[]; // p.ej. filas con lat/lon no numérico
};

export type ParseResult =
  | { ok: true; data: ParseSuccess }
  | { ok: false; error: ParseError };

/**
 * Parsea el CSV exportado desde Numbers/Excel a un array de SaleRow.
 * Devuelve un Result tagged-union — el caller decide cómo mostrar
 * cada error sin tener que parsear strings.
 */
export async function parseCsv(file: File): Promise<ParseResult> {
  // Leemos como texto primero para detectar la fila de header.
  const text = await file.text();
  // Numbers a veces exporta líneas vacías al inicio — las saltamos
  // sumando un offset al primer header.
  const cleaned = text.replace(/^(\s*\r?\n)+/, "");

  const parsed = Papa.parse<string[]>(cleaned, {
    skipEmptyLines: "greedy",
  });
  if (parsed.errors.length > 0 && parsed.data.length === 0) {
    return { ok: false, error: { kind: "papaparse", message: parsed.errors[0].message } };
  }
  if (parsed.data.length === 0) {
    return { ok: false, error: { kind: "no-data" } };
  }

  // Buscamos la fila de header: la primera que tenga al menos 4 de
  // las columnas conocidas. Acepta header en fila 0, 1 o 2 (Numbers
  // mete a veces filas decorativas al inicio).
  let headerIdx = -1;
  let mapping: (Exclude<keyof SaleRow, "id"> | null)[] = [];
  for (let i = 0; i < Math.min(5, parsed.data.length); i++) {
    const row = parsed.data[i] ?? [];
    const m = row.map(matchHeader);
    const known = m.filter((x) => x !== null).length;
    if (known >= 4) {
      headerIdx = i;
      mapping = m;
      break;
    }
  }
  if (headerIdx < 0) {
    return {
      ok: false,
      error: {
        kind: "missing-headers",
        missing: ["TRANSPORTISTA", "CLIENTE", "EA.LATITUD", "EA.LONGITUD", "Suma de VALOR VENTA"],
      },
    };
  }

  // Validar columnas requeridas presentes.
  const required: Exclude<keyof SaleRow, "id">[] = ["transportista", "cliente", "lat", "lon", "valorVenta"];
  const missing = required.filter((k) => !mapping.includes(k));
  if (missing.length > 0) {
    return { ok: false, error: { kind: "missing-headers", missing } };
  }

  const rows: SaleRow[] = [];
  const warnings: string[] = [];
  let nextId = 0;
  for (let i = headerIdx + 1; i < parsed.data.length; i++) {
    const cells = parsed.data[i] ?? [];
    const out: Partial<SaleRow> = { id: nextId };
    for (let c = 0; c < mapping.length; c++) {
      const key = mapping[c];
      if (!key) continue;
      const raw = (cells[c] ?? "").toString().trim();
      if (key === "lat" || key === "lon" || key === "valorVenta") {
        const n = parseFloat(raw.replace(",", "."));
        (out as Record<string, unknown>)[key] = Number.isFinite(n) ? n : NaN;
      } else if (key === "pesoKg" || key === "volumenM3") {
        // Opcionales: si la celda está vacía o no parsea, omitimos
        // el campo (queda undefined) y el consumer hace fallback.
        if (raw === "") continue;
        const n = parseFloat(raw.replace(",", "."));
        if (Number.isFinite(n) && n >= 0) {
          (out as Record<string, unknown>)[key] = n;
        }
      } else {
        (out as Record<string, unknown>)[key] = raw;
      }
    }
    // Filas completamente vacías → skip silencioso.
    if (!out.transportista && !out.cliente) continue;
    if (!out.transportista) {
      warnings.push(`Fila ${i + 1}: sin transportista, descartada`);
      continue;
    }
    rows.push(out as SaleRow);
    nextId++;
  }
  if (rows.length === 0) {
    return { ok: false, error: { kind: "no-data" } };
  }
  return { ok: true, data: { rows, warnings } };
}
